// Frontend/geniac/src/hooks/useOrganizationStatus.ts
// Hook pour gérer le statut de l'organisation (ACTIVE, OVER_QUOTA, etc.)

import { useState, useEffect } from 'react';
import { getOrganizationStatus } from '../api/subscriptions';

export type OrganizationStatus = 'ACTIVE' | 'OVER_QUOTA' | 'TRIAL_EXPIRED' | 'SUSPENDED' | 'CANCELLED';

interface OverQuotaDetails {
  projects_over?: number;
  projects_current?: number;
  projects_limit?: number;
  architectures_over?: number;
  architectures_current?: number;
  architectures_limit?: number;
}

interface OrganizationInfo {
  id: number;
  name: string;
  status: OrganizationStatus;
  over_quota_details: OverQuotaDetails | null;
  status_updated_at: string;
}

interface SubscriptionInfo {
  plan: string;
  pending_plan: string | null;
  downgrade_effective_at: string | null;
}

interface Usage {
  projects: number;
  architectures: number;
  deployments_this_month: number;
  pipeline_minutes_this_month: number;
  api_calls_this_month: number;
  users: number;
}

interface UseOrganizationStatusResult {
  organization: OrganizationInfo | null;
  subscription: SubscriptionInfo | null;
  usage: Usage | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  isOverQuota: boolean;
  canCreate: boolean;
}

export function useOrganizationStatus(): UseOrganizationStatusResult {
  const [organization, setOrganization] = useState<OrganizationInfo | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getOrganizationStatus();

      setOrganization(data.organization);
      setSubscription(data.subscription);
      setUsage(data.usage);
    } catch (err: any) {
      console.error('Error fetching organization status:', err);
      setError(err.message || 'Failed to fetch organization status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const isOverQuota = organization?.status === 'OVER_QUOTA';
  const canCreate = organization?.status === 'ACTIVE';

  return {
    organization,
    subscription,
    usage,
    loading,
    error,
    refresh: fetchStatus,
    isOverQuota,
    canCreate
  };
}
